import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { cartActions } from "../../../store/cart-slice";
import { ProductContext } from "../../../context/ProductContext";
import { getProductTitle } from "../../../utils/productDisplayName";

/* Minimal list row - no card box, hairline divider, understated controls */

const Row = styled.figure`
  position: relative;
  width: 100%;
  padding: var(--spacing-md) 0;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  border-bottom: 1px solid var(--bg-300);
`;
const Picture = styled.picture`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
`;

const Image = styled.img`
  height: 100%;
  width: 100%;
  object-fit: contain;
  display: block;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;
const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-sm);
`;
const ProductName = styled.h4`
  margin: 0;
  padding: 0;
  font-size: var(--font-size-small);
  font-weight: 400;
  color: var(--text-100);
  line-height: 1.4;
  letter-spacing: 0.2px;
`;
const RemoveText = styled.span`
  cursor: pointer;
  flex-shrink: 0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-200);
  border-bottom: 1px solid transparent;
  &:hover {
    color: var(--error-color);
    border-bottom-color: var(--error-color);
  }
`;
const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Stepper = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid var(--bg-300);
`;
const StepButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-100);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  &:hover {
    background-color: var(--text-100);
    color: var(--bg-100);
  }
`;
const QtyInput = styled.input`
  width: 28px;
  text-align: center;
  font-size: var(--font-size-small);
  color: var(--text-100);
  background: none;
  border: none;
  border-left: 1px solid var(--bg-300);
  border-right: 1px solid var(--bg-300);
  padding: 0;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type="number"] {
    -moz-appearance: textfield;
  }
`;
const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const UnitPrice = styled.span`
  font-size: 11px;
  color: var(--text-200);
`;
const LineTotal = styled.span`
  font-size: var(--font-size-base);
  font-weight: 400;
  color: var(--text-100);
`;

const CartProduct = ({ item }) => {
  const { t } = useTranslation();
  const { currencyTag } = useContext(ProductContext);
  const [inputValue, setInputValue] = useState(item.quantity);
  const dispatch = useDispatch();

  const deleteCartItem = (id) => {
    dispatch(cartActions.removeFromCart(id));
  };

  const handleAddOne = () => {
    const newQuantity = inputValue + 1;
    dispatch(
      cartActions.updateCart({
        product: item.product,
        quantity: newQuantity,
      })
    );
  };
  const handleRemoveOne = () => {
    const newQuantity = inputValue - 1;
    if (newQuantity === 0) {
      deleteCartItem(item.product.id);
    }
    dispatch(
      cartActions.updateCart({
        product: item.product,
        quantity: newQuantity,
      })
    );
  };
  useEffect(() => {
    setInputValue(item.quantity);
  }, [item.quantity]);

  const handleChange = (event) => {
    const value = Math.max(1, Math.min(999, Number(event.target.value)));
    setInputValue(value);
    dispatch(
      cartActions.updateCart({
        product: item.product,
        quantity: value,
      })
    );
  };

  const primaryImage =
    item.product.images.find((img) => !img.is_primary) ||
    item.product.images.find((img) => img.is_primary);

  return (
    <Row>
      <Picture>
        {primaryImage && (
          <Image
            src={primaryImage.thumbnail}
            alt={getProductTitle(item.product.category_name, item.product.name)}
            srcSet={`${primaryImage.thumbnail} 320w, ${primaryImage.large} 480w, ${primaryImage.large} 800w`}
            sizes="(max-width: 320px) 300px, (max-width: 480px) 440px, 800px"
            loading="lazy"
          />
        )}
      </Picture>
      <Right>
        <TopRow>
          <ProductName>
            {getProductTitle(item.product.category_name, item.product.name)}{" "}
            {item.product.nicotine}MG
          </ProductName>
          <RemoveText
            role="button"
            tabIndex={0}
            aria-label={t("BUTTONS.REMOVE")}
            onClick={() => deleteCartItem(item.product.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                deleteCartItem(item.product.id);
              }
            }}
          >
            {t("BUTTONS.REMOVE")}
          </RemoveText>
        </TopRow>
        <BottomRow>
          <Stepper>
            <StepButton
              onClick={handleRemoveOne}
              aria-label={t("ARIA.DECREASE_QUANTITY")}
            >
              −
            </StepButton>
            <QtyInput
              type="number"
              max="999"
              min="0"
              value={inputValue}
              onChange={handleChange}
              aria-label={t("PRODUCT_CARD.CHOOSE_QUANTITY")}
            />
            <StepButton
              onClick={handleAddOne}
              aria-label={t("ARIA.INCREASE_QUANTITY")}
            >
              +
            </StepButton>
          </Stepper>
          <PriceBlock>
            <UnitPrice>
              {currencyTag}
              {item.product.discount_price.toFixed(2)} / {t("PRODUCT_CARD.PIECE")}
            </UnitPrice>
            <LineTotal>
              {currencyTag}
              {(item.product.discount_price * item.quantity).toFixed(2)}
            </LineTotal>
          </PriceBlock>
        </BottomRow>
      </Right>
    </Row>
  );
};

export default CartProduct;
